import { useCallback, useEffect } from 'react';
import { parse, format } from 'date-fns';

import { ISO_FORMAT, getEndOfDay } from '../../utilities';
import { DatepickerComponent } from './Component';
import type { DatepickerComponentProps } from './Component';

export type DatepickerProps = Omit<
  DatepickerComponentProps,
  'value' | 'onChange'
> & {
  value?: string;
  onChange?: (newValue: string) => void;
  valueFormat?: string;

  disabled?: boolean;
  endOfDay?: boolean;
  cleanOnDisable?: boolean;
};

export const Datepicker: React.FC<DatepickerProps> = ({
  value,
  onChange,
  valueFormat = ISO_FORMAT,
  disabled,
  cleanOnDisable = false,
  endOfDay = false,
  ...rest
}) => {
  const thisValue = value ? parse(value, valueFormat, new Date()) : undefined;
  const handleChange = useCallback(
    (newValue: Date | null) => {
      const formattedValue = newValue ? format(newValue, valueFormat) : '';
      const result = endOfDay ? getEndOfDay(formattedValue) : formattedValue;

      onChange?.(result);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valueFormat, endOfDay]
  );

  useEffect(
    () => {
      if (!cleanOnDisable || !disabled) return;

      onChange?.('');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cleanOnDisable, disabled]
  );

  return (
    <DatepickerComponent
      disabled={disabled}
      {...rest}
      value={thisValue}
      onChange={handleChange}
    />
  );
};
